import { formatMoney } from '@/lib/format';

/*
  How the sales figures read (ALS-85). The API has already summed and rounded every amount, so
  these only format: nothing here adds, which is half of what keeps the dashboard card and the
  report from disagreeing.
*/

/**
 * "£612k", "£1.31M", "£4.9k": the prototype's compact money, for chart axes, tiles and rankings.
 * One decimal while it means something; anything under £50 reads "£0" rather than as "£0k", which
 * looks like a unit error.
 */
export function formatPoundsCompact(pounds: number): string {
    const sign = pounds < 0 ? '-' : '';
    const value = Math.abs(pounds);
    if (value < 50) return '£0';
    if (value >= 1_000_000) return `${sign}£${trim(value / 1_000_000)}M`;
    return `${sign}£${trim(value / 1_000)}k`;
}

/** "£2,200", whole pounds, for the one big figure on a card. */
export function formatPounds(pounds: number): string {
    return formatMoney(Math.round(pounds), 'GBP', 0);
}

export function formatPercent(value: number): string {
    return `${value}%`;
}

export type ChangeTone = 'up' | 'down' | 'flat';

export interface FormattedChange {
    text: string;
    tone: ChangeTone;
}

/**
 * Why a figure has no change (ALS-422), as the API names it. A null change is not always a missing
 * period: a margin % has no base when the period's refunds exceed its sales, and a figure that is
 * zero or absent on either side has nothing to be a change of.
 */
export const CHANGE_GAP = {
    NoPriorPeriod: 'NoPriorPeriod',
    RefundsExceedSales: 'RefundsExceedSales',
    NotComparable: 'NotComparable',
} as const;

export type ChangeGap = (typeof CHANGE_GAP)[keyof typeof CHANGE_GAP];

const GAP_TEXT: Record<ChangeGap, string> = {
    NoPriorPeriod: 'No prior period',
    RefundsExceedSales: 'Refunds exceed sales',
    NotComparable: "Can't be compared",
};

/**
 * A change against the period before: "+12.4%", "-3%", or for a margin "+2pt". Null is said
 * rather than drawn as a zero, in the words of its gap; with none given, there was nothing before.
 */
export function formatChange(
    change: number | null,
    unit: '%' | 'pt' = '%',
    gap: ChangeGap | null = CHANGE_GAP.NoPriorPeriod,
): FormattedChange {
    if (change === null) {
        return { text: GAP_TEXT[gap ?? CHANGE_GAP.NoPriorPeriod], tone: 'flat' };
    }
    const tone: ChangeTone = change > 0 ? 'up' : change < 0 ? 'down' : 'flat';
    const sign = change > 0 ? '+' : change < 0 ? '-' : '';
    return { text: `${sign}${Math.abs(change).toLocaleString('en-GB')}${unit}`, tone };
}

function trim(value: number): string {
    return String(Math.round(value * 10) / 10);
}
